import React from 'react';
import { Form, Input, Select, Button, message, Row, Col } from 'antd';
import { useCreateUserMutation } from '@/hooks/api/users';

const { Option } = Select;

interface CreateUserFormData {
  name: string;
  email: string;
  role: string;
  password: string;
  confirmPassword: string;
}

interface CreateUserFormProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export const CreateUserForm: React.FC<CreateUserFormProps> = ({ onSuccess, onCancel }) => {
  const [form] = Form.useForm();
  const [createUser, { isLoading }] = useCreateUserMutation();

  const handleSubmit = async (values: CreateUserFormData) => {
    try {
      // Get client_id from session storage (current organization)
      const clientId = window.sessionStorage.getItem('client_id') || '';

      const requestBody = {
        email: values.email,
        user_name: values.name,
        name: values.name,
        nick_name: values.name.split(' ')[0], // Use first name as nickname
        password: values.password,
        role: values.role,
        database: 'company_db',
        client_id: clientId,
      };

      const result = await createUser(requestBody).unwrap();

      if (result.success) {
        message.success('User created successfully!');
        form.resetFields();
        onSuccess();
      } else {
        message.error(result.message || 'Failed to create user');
      }
    } catch (error: any) {
      console.error('Error creating user:', error);
      message.error(error?.data?.message || 'Failed to create user');
    }
  };

  return (
    <div className="p-6">
      <h2 className="text-xl font-semibold mb-6">Add a new member</h2>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
        initialValues={{ role: 'admin' }}
      >
        <Row gutter={24}>
          {/* First Column: Name, Email, Password, Password Verification */}
          <Col span={12}>
            <Form.Item
              label="Name"
              name="name"
              rules={[{ required: true, message: 'Please input the name!' }]}
            >
              <Input placeholder="Enter full name" />
            </Form.Item>

            <Form.Item
              label="Email"
              name="email"
              rules={[
                { required: true, message: 'Please input the email!' },
                { type: 'email', message: 'Please enter a valid email!' },
              ]}
            >
              <Input placeholder="Enter email address" />
            </Form.Item>

            <Form.Item
              label="Password"
              name="password"
              rules={[
                { required: true, message: 'Please input the password!' },
                { min: 8, message: 'Password must be at least 8 characters long!' },
              ]}
            >
              <Input.Password placeholder="Enter password" />
            </Form.Item>

            <Form.Item
              label="Confirm Password"
              name="confirmPassword"
              dependencies={['password']}
              rules={[
                { required: true, message: 'Please confirm the password!' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('password') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('The two passwords do not match!'));
                  },
                }),
              ]}
            >
              <Input.Password placeholder="Confirm password" />
            </Form.Item>
          </Col>

          {/* Second Column: Role */}
          <Col span={12}>
            <Form.Item
              label="Role"
              name="role"
              rules={[{ required: true, message: 'Please select a role!' }]}
            >
              <Select value="admin" disabled>
                <Option value="view only">View Only</Option>
                <Option value="admin">Admin</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <div className="flex justify-end gap-3 mt-8">
          <Button onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            type="primary"
            htmlType="submit"
            loading={isLoading}
            className="bg-black text-white hover:bg-gray-800"
          >
            Add
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default CreateUserForm;
